import React, { useState } from 'react';
import { Upload, Star } from 'lucide-react';
import { useR2Upload } from '../../hooks/useR2Upload';

const API = process.env.REACT_APP_BACKEND_URL;

const VideoUploadForm = ({ token, onUploaded }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [isExclusive, setIsExclusive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const videoUploader = useR2Upload(token);
  const thumbUploader = useR2Upload(token);

  const reset = () => {
    setTitle('');
    setDescription('');
    setVideoFile(null);
    setThumbnailFile(null);
    setIsExclusive(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!videoFile) {
      setError('Please choose a video file.');
      return;
    }

    setSubmitting(true);
    try {
      const videoKey = await videoUploader.upload(videoFile, 'video');
      const thumbKey = thumbnailFile
        ? await thumbUploader.upload(thumbnailFile, 'thumbnail')
        : null;

      const res = await fetch(`${API}/api/videos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          description,
          video_key: videoKey,
          thumbnail_key: thumbKey,
          is_exclusive: isExclusive,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to save video (${res.status})`);
      }

      reset();
      onUploaded();
      alert('Video uploaded successfully!');
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const overallProgress = videoUploader.uploading
    ? videoUploader.progress
    : thumbUploader.uploading
    ? thumbUploader.progress
    : 0;

  return (
    <div className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg p-6 mb-8">
      <h3 className="text-xl font-black text-white mb-4 flex items-center gap-2">
        <Upload className="h-5 w-5" />
        Upload New 18+ Video
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Videos upload directly to Cloudflare R2 - large files supported, survives redeploys.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="video-upload-form">
        {error && (
          <div className="bg-red-900/30 border border-red-500/50 text-red-300 px-3 py-2 rounded text-sm" data-testid="video-upload-error">
            {error}
          </div>
        )}

        <div>
          <label className="block text-white font-bold mb-2 text-sm">Video Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-black/50 border border-red-900/50 text-white px-4 py-3 rounded focus:outline-none focus:border-red-500"
            placeholder="Enter video title"
            required
            data-testid="video-title-input"
          />
        </div>

        <div>
          <label className="block text-white font-bold mb-2 text-sm">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full bg-black/50 border border-red-900/50 text-white px-4 py-3 rounded focus:outline-none focus:border-red-500 h-24"
            placeholder="Enter video description"
            required
            data-testid="video-description-input"
          />
        </div>

        <div>
          <label className="block text-white font-bold mb-2 text-sm">Video File</label>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => setVideoFile(e.target.files[0])}
            className="w-full bg-black/50 border border-red-900/50 text-white px-4 py-3 rounded focus:outline-none focus:border-red-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-red-600 file:text-white file:cursor-pointer hover:file:bg-red-700"
            required
            data-testid="video-file-input"
          />
          <p className="text-xs text-gray-500 mt-1">MP4, WebM, MOV - uploads straight to Cloudflare R2.</p>
        </div>

        <div>
          <label className="block text-white font-bold mb-2 text-sm">Thumbnail (Optional)</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setThumbnailFile(e.target.files[0])}
            className="w-full bg-black/50 border border-red-900/50 text-white px-4 py-3 rounded focus:outline-none focus:border-red-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-zinc-700 file:text-white file:cursor-pointer hover:file:bg-zinc-600"
            data-testid="video-thumbnail-input"
          />
        </div>

        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="video-exclusive-toggle-label">
          <input
            type="checkbox"
            checked={isExclusive}
            onChange={(e) => setIsExclusive(e.target.checked)}
            className="h-5 w-5 rounded border-yellow-500/60 bg-black/50 accent-yellow-400"
            data-testid="video-exclusive-toggle"
          />
          <span className="flex items-center gap-2 text-white font-bold text-sm">
            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
            Mark as <span className="text-yellow-400">EXCLUSIVE</span>
          </span>
        </label>

        {(videoUploader.uploading || thumbUploader.uploading) && (
          <div data-testid="video-upload-progress">
            <p className="text-xs text-gray-400 mb-1">
              Uploading {videoUploader.uploading ? 'video' : 'thumbnail'}… {overallProgress}%
            </p>
            <div className="w-full h-2 bg-zinc-800 rounded overflow-hidden">
              <div
                className="h-full bg-red-500 transition-all"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !videoFile}
          data-testid="video-upload-submit-btn"
          className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Uploading…' : 'Upload Video'}
        </button>
      </form>
    </div>
  );
};

export default VideoUploadForm;
